import { useState } from "react";
import { generateYoutubeThumbnail } from "common/utils/generateYoutubeThumbnail";
import { getCampaignDefaultImage } from "common/utils/getCampaignDefauiltImage";
import { CampaignModelMapper } from "features/campaign/domain/models/campaignModel";
import useKroCountdown from "features/components/countdown/kroCountdown";
import { useNavigate } from "react-router-dom";
import KroShareModal from "features/components/modals/kroShareModal";
import { KroCampaignStatusBadge, KroCategoryBadge } from "features/components/display/kroBadges";
import { formatCurrency } from "common/utils/currencyFormater";
import KroProgressBar, { variant } from "features/components/progress/kroProgressBar";
import DonorAvatars from "features/campaign/presentation/components/campaignDetails/campaignDonorAvatar";
import Routes from "router/routes";


const DashboardCampaignCard = ({
    campaign,
}: {
    campaign: CampaignModelMapper;
}) => {
    const countdown = useKroCountdown(campaign?.endDate);

    const navigate = useNavigate();

    const campaignImage =
        generateYoutubeThumbnail(campaign?.videoUrl ?? "") ??
        (campaign?.imageUrls?.[0] || null) ??
        getCampaignDefaultImage(campaign?.category);

    const ActionButton = ({
        title,
        onClick,
        className,
        icon,
    }: {
        title: string;
        className: string;
        onClick: () => void;
        icon?: string;
    }) => {
        return (
            <button
                onClick={onClick}
                className={`flex h-[36px] w-[133px] items-center justify-center rounded-[32px] border border-neutral-200 bg-neutral-50 px-[9px] py-[16px] text-[12px] font-medium leading-[18px] tracking-[-0.5%] text-[#0F0F0F] hover:bg-neutral-100 ${className}`}
            >
                {title}
                {icon && (
                    <img src={icon} alt={title} className="ml-[4px] h-[16px] w-[16px]" />
                )}
            </button>
        );
    };

    const [isShareModalOpen, setShareModalOpen] = useState<boolean>(false);
    const handleShareModalClose = () => {
        setShareModalOpen(false);
    };

    const handleShareModalOpen = () => {
        setShareModalOpen(true);
    };

    return (
        <>
            <KroShareModal
                campaignId={campaign?.slug === null ? campaign?.id : campaign?.slug}
                isOpen={isShareModalOpen}
                onClose={handleShareModalClose}
            />
            <div
                onClick={() => {
                    // navigate(Routes.CAMPAIGN_BY_SLUG(campaign?.slug ?? campaign?.id ?? ""));
                }}
                className={`flex min-h-[474px] w-full cursor-pointer select-none flex-col rounded-[24px] border border-neutral-200 lg:min-h-[288px] lg:max-w-[1248px] lg:flex-row lg:p-[24px]`}
            >
                <img
                    src={campaignImage}
                    alt={campaignImage}
                    className="h-[165px] w-full rounded-t-[24px] object-cover lg:h-[240px] lg:max-w-[320px] lg:rounded-[24px]"
                />
                <div className="mx-[12px] mt-[24px] flex flex-col lg:ml-[24px] lg:w-full lg:max-w-full">
                    <p className="font-inter text-[20px] font-medium leading-[100%] tracking-[-2.5%] text-[#000929] lg:text-[24px] lg:font-semibold lg:tracking-[0%]">
                        {campaign?.title}
                    </p>
                    <div className="mt-[12px] flex flex-row gap-[12px]">
                        {campaign?.category && (
                            <KroCategoryBadge category={campaign?.category} />
                        )}
                        <KroCampaignStatusBadge status={campaign?.status} />
                    </div>
                    <div className="mt-[32.5px]">
                        <div className="mb-[10px] flex flex-row items-center justify-between">
                            <span className="text-body-lg font-semibold leading-[20px] tracking-[-0.006em] text-neutral-900">
                                {
                                    <span>
                                        {formatCurrency(campaign?.amountRaised ?? "", true)}
                                    </span>
                                }{" "}
                                <span className="font-inter text-body-md font-normal text-neutral-600">
                                    {campaign?.targetAmount !== 0 ? "raised of" : "raised"}
                                </span>{" "}
                                {campaign?.targetAmount !== 0 && (
                                    <span className="text-body-md font-normal text-neutral-600">
                                        {formatCurrency(
                                            campaign?.targetAmount?.toString() ?? "",
                                            true,
                                        )}
                                    </span>
                                )}
                            </span>
                            <span className="font-inter text-body-md font-medium leading-[16px] text-neutral-900">
                                {campaign?.status === "ended" ? "Campaign Ended" : countdown}
                            </span>
                        </div>
                        {
                            <KroProgressBar
                                height="h-3"
                                customStyle=""
                                varient={variant.gradient}
                                amountRaised={campaign?.amountRaised?.toString() ?? ""}
                                amountToBeRasied={campaign?.targetAmount?.toString() ?? ""}
                            />
                        }
                        <div className="mt-[16px]">
                            <DonorAvatars
                                campaign={campaign}
                                count={campaign?.donations?.length ?? 0}
                            />
                        </div>
                        <div className="mt-[20px] flex flex-col gap-[8px] lg:flex-row">
                            <ActionButton
                                title="Edit"
                                className="hidden lg:flex"
                                onClick={() => {
                                    navigate(
                                        Routes.EDIT_CAMPAIGN(campaign?.slug ?? campaign?.id ?? ""),
                                    );
                                }}
                            />
                            <ActionButton
                                title="View"
                                className="hidden lg:flex"
                                onClick={() => {
                                    navigate(
                                        Routes.END_CAMPAIGN(campaign?.slug ?? campaign?.id ?? ""),
                                    );
                                }}
                            />
                            <div className="flex w-full flex-row gap-[8px] lg:hidden">
                                <ActionButton
                                    title="Edit"
                                    onClick={() => {
                                        navigate(
                                            Routes.EDIT_CAMPAIGN(
                                                campaign?.slug ?? campaign?.id ?? "",
                                            ),
                                        );
                                    }}
                                    className="flex-1"
                                />
                                <ActionButton
                                    title="View"
                                    onClick={() => {
                                        navigate(
                                            Routes.END_CAMPAIGN(campaign?.slug ?? campaign?.id ?? ""),
                                        );
                                    }}
                                    className="flex-1"
                                />
                            </div>
                            <div className="flex w-full sm:hidden sm:w-auto">
                                <ActionButton
                                    title="Share"
                                    icon="/assets/icons/share_icon_black.svg"
                                    onClick={() => {
                                        handleShareModalOpen();
                                    }}
                                    className="flex-1"
                                />
                            </div>
                            <div className="hidden sm:flex sm:w-auto">
                                <ActionButton
                                    title="Share"
                                    icon="/assets/icons/share_icon_black.svg"
                                    onClick={() => {
                                        handleShareModalOpen();
                                    }}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DashboardCampaignCard;